<i18n>
{
  "fr": {
    "configuratorLayerEditorModalCropImage": {
      "input": {
        "zoom": {
          "label": "Zoom"
        },
        "rotation": {
          "label": "Rotation"
        }
      },
      "action": {
        "submit": {
          "label": "Valider",
          "title": "@:configuratorLayerEditorModalCropImage.action.submit.label"
        },
        "cancel": {
          "label": "Retour",
          "title": "@:configuratorLayerEditorModalCropImage.action.cancel.label"
        }
      }
    }
  }
}
</i18n>

<template>
  <form class="configurator-layer-editor-modal-crop-image" @submit="onSubmit">
    <div class="configurator-layer-editor-modal-crop-image__body">
      <VueCropper
        ref="cropper"
        class="configurator-layer-editor-modal-crop-image__cropper"
        :src="customization.url"
        alt="source image"
        :aspect-ratio="zone.width / zone.height"
        :ready="onReady"
        :check-orientation="false"
      />
    </div>

    <div class="configurator-layer-editor-modal-crop-image__footer">
      <div class="configurator-layer-editor-modal-crop-image__footer-container">
        <div
          class="configurator-layer-editor-modal-crop-image__range-item configurator-layer-editor-modal-crop-image__range-item--zoom"
        >
          <div
            class="configurator-layer-editor-modal-crop-image__range-item-label-row"
          >
            <label
              :for="getInputId('zoom')"
              class="configurator-layer-editor-modal-crop-image__range-item-label"
              v-text="
                $t('configuratorLayerEditorModalCropImage.input.zoom.label')
              "
            ></label>

            <output
              class="configurator-layer-editor-modal-crop-image__range-item-value"
              :for="getInputId('zoom')"
              v-text="zoom"
            ></output>
          </div>

          <input
            :id="getInputId('zoom')"
            v-model.number="zoom"
            min="0.1"
            max="1.9"
            step="0.1"
            type="range"
            class="configurator-layer-editor-modal-crop-image__range-item-element"
          />
        </div>

        <div
          class="configurator-layer-editor-modal-crop-image__range-item configurator-layer-editor-modal-crop-image__range-item--rotation"
        >
          <div
            class="configurator-layer-editor-modal-crop-image__range-item-label-row"
          >
            <label
              :for="getInputId('rotation')"
              class="configurator-layer-editor-modal-crop-image__range-item-label"
              v-text="
                $t('configuratorLayerEditorModalCropImage.input.rotation.label')
              "
            ></label>

            <output
              class="configurator-layer-editor-modal-crop-image__range-item-value"
              :for="getInputId('rotation')"
              v-text="rotation"
            ></output>
          </div>

          <input
            :id="getInputId('rotation')"
            v-model.number="rotation"
            step="1"
            min="-180"
            max="180"
            type="range"
            class="configurator-layer-editor-modal-crop-image__range-item-element"
          />
        </div>
      </div>
    </div>

    <div class="configurator-layer-editor-modal-crop-image__actions">
      <div
        class="configurator-layer-editor-modal-crop-image__actions-container"
      >
        <BtnToolbar full-width-equal>
          <BaseBtn
            :title="
              $t('configuratorLayerEditorModalCropImage.action.cancel.title')
            "
            @click="goBack"
            v-text="
              $t('configuratorLayerEditorModalCropImage.action.cancel.label')
            "
          />

          <BaseBtn
            type="submit"
            secondary
            :title="
              $t('configuratorLayerEditorModalCropImage.action.submit.title')
            "
            v-text="
              $t('configuratorLayerEditorModalCropImage.action.submit.label')
            "
          />
        </BtnToolbar>
      </div>
    </div>
  </form>
</template>

<script>
import BaseBtn from '../../../BaseBtn';
import BtnToolbar from '../../../BtnToolbar';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import { AutoIncrementIdFactory } from '../../../../utils/AutoIncrement';
import { canvasToBlob, fitArea } from '../../../../utils/ImageUtils';

const configuratorLayerEditorModalCropImageIdGenerator =
  AutoIncrementIdFactory(1);

export default {
  name: 'ConfiguratorLayerEditorModalCropImage',
  components: { BtnToolbar, BaseBtn, VueCropper },
  props: {
    customization: {
      type: Object,
      required: true,
    },
    group: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      id: configuratorLayerEditorModalCropImageIdGenerator.next().value,
      zoom: 1,
      rotation: 0,
      isReady: false,
    };
  },
  computed: {
    zone() {
      return this.group.zoneMap[this.customization.templateZoneId];
    },
  },
  methods: {
    getInputId(field) {
      return `configurator-layer-editor-modal-crop-image__${field}--${this.id}`;
    },
    async onSubmit(e) {
      e.preventDefault();

      const canvas = this.$refs.cropper.getCroppedCanvas();
      const imageBlob = await canvasToBlob(canvas);

      this.$emit('submit', imageBlob);
    },
    goBack() {
      this.$emit('go-back');
    },
    onReady() {
      if (!this.isReady) {
        this.isReady = true;
        const cropper = this.$refs.cropper;

        // Increase crop box size as much as possible (but fit image)
        const canvasData = cropper.getCanvasData();
        const { width, height } = fitArea(this.zone, canvasData, true);

        const cropSettings = {
          width,
          height,
          left: canvasData.left + (canvasData.width - width) / 2,
          top: canvasData.top + (canvasData.height - height) / 2,
        };

        cropper.setCropBoxData(cropSettings);

        this.$watch(
          'rotation',
          newValue => {
            cropper.rotateTo(newValue);
          },
          { immediate: true }
        );

        this.$watch('zoom', (newValue, oldValue) => {
          const deltaValue = parseFloat(
            (newValue - (oldValue || 0)).toFixed(1)
          );

          cropper.relativeZoom(deltaValue);
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-layer-editor-modal-crop-image {
  $block-selector: &;
  background: $color-white;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  &__body {
    flex: 1;
    min-height: 0;
  }

  &__actions,
  &__footer {
    flex: none;
  }

  &__actions-container,
  &__footer-container {
    margin: 1em;
  }

  &__cropper {
    height: 100%;
    overflow: hidden;
  }

  &__range-item {
    margin-bottom: 1em;

    @include breakpoint(min-height $breakpoint-xs) {
      margin-bottom: 2em;
    }
  }

  &__range-item-label-row {
    display: flex;
    margin-bottom: 0.5em;
  }

  &__range-item-label {
    flex: none;

    &::before {
      $icon-font-size: 0.75em;
      @extend %icon;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      width: 2em;
      height: 2em;
      border-radius: 50%;
      font-size: 0.75em;
      background: $color-gray-gallery-2;
      margin-right: 1em * parse-int($icon-font-size);

      #{$block-selector}__range-item--rotation & {
        @extend %icon--rotate;
      }

      #{$block-selector}__range-item--zoom & {
        @extend %icon--resize;
      }
    }
  }

  &__range-item-value {
    flex: none;
    margin-left: auto;
    color: $color-gray-dove;
  }

  &__range-item-element {
    $range-thumb-height: 1.5em;
    $range-track-height: 0.4em;
    $vertical-margin: math.div($range-thumb-height - $range-track-height, 2);
    $range-progress-color: $color-primary;
    appearance: none;
    width: 100%;
    height: max($range-thumb-height, $range-track-height);
    display: block;

    // Duplicate code instead of concatenate to prevent browser from skipping the whole selector
    @mixin range-slider-thumb() {
      border: solid 1px $color-gray-alto-light;
      background: $color-white;
      width: $range-thumb-height;
      height: $range-thumb-height;
      border-radius: 50%;
      cursor: pointer;
      margin-top: -$vertical-margin;
      transition: box-shadow $default-transition-duration;
    }

    @mixin range-slider-track() {
      height: $range-track-height;
      background: $color-gray-gallery-2;
      border-radius: math.div($range-track-height, 2);
    }

    @mixin range-slider-thumb-hover() {
      box-shadow: 0 0 0.25em $color-gray-alto-light;
    }

    @mixin range-slider-thumb-focus() {
      box-shadow: 0 0 0.25em $color-gray-dove;
    }

    @mixin range-slider-progress() {
      background-color: $range-progress-color;
      border-top-left-radius: math.div($range-track-height, 2);
      border-bottom-left-radius: math.div($range-track-height, 2);
    }

    // Webkit
    &::-webkit-slider-thumb {
      appearance: none;
      @include range-slider-thumb();
    }

    &::-webkit-slider-runnable-track {
      @include range-slider-track();
    }

    // Firefox
    &::-moz-range-thumb {
      @include range-slider-thumb();
    }

    &::-moz-range-track {
      @include range-slider-track();
    }

    &::-moz-range-progress {
      @include range-slider-progress();
    }

    // Trident/EdgeHTML
    &::-ms-thumb {
      @include range-slider-thumb();
    }

    &::-ms-track {
      width: 100%;
      cursor: pointer;
      background: transparent;
      border: none;
      color: transparent;
      @include range-slider-track();
    }

    &::-ms-fill-lower {
      @include range-slider-progress();
    }

    &:hover {
      // Webkit
      &::-webkit-slider-thumb {
        @include range-slider-thumb-hover();
      }

      // Firefox
      &::-moz-range-thumb {
        @include range-slider-thumb-hover();
      }

      // Trident/EdgeHTML
      &::-ms-thumb {
        @include range-slider-thumb-hover();
      }
    }

    &:focus {
      outline: none;

      // Webkit
      &::-webkit-slider-thumb {
        @include range-slider-thumb-focus();
      }

      // Firefox
      &::-moz-range-thumb {
        @include range-slider-thumb-focus();
      }

      // Trident/EdgeHTML
      &::-ms-thumb {
        @include range-slider-thumb-focus();
      }
    }

    &:disabled {
      &::-webkit-slider-thumb,
      &::-moz-range-thumb,
      &::-ms-thumb,
      &::-webkit-slider-runnable-track,
      &::-ms-fill-lower,
      &::-ms-fill-upper {
        cursor: not-allowed;
      }
    }
  }
}
</style>
